import { toast } from "react-toastify";

import API from "../../api";
import { getToken } from "../../auth";
import { useMerchant } from "../../../store/global/useMerchant";

import { CRM_PET_DEFAULT_FILTER, useCrmPet } from "../../../store/crmPet/useCrmPet";
import { FAIXAS_DEFAULT } from "../../../store/crmPet/constants";
import {
  CRM_PET_RULES,
  formatContactHistory,
  formatCustomerOpportunities,
  formatCrmPetConfig,
  formatOpportunitiesResponse,
} from "../../formatResponses/crmPet";

export class CrmPet {
  actions = useCrmPet.getState().actions;

  listContext() {
    const merchant = useMerchant.getState().data.merchant;
    return {
      token: getToken(),
      store: typeof localStorage === "undefined" ? null : localStorage.getItem("STORE"),
      merchantId: merchant?._id || merchant?.id || null,
    };
  }

  sameListContext(left, right) {
    return left.token === right.token && left.store === right.store && left.merchantId === right.merchantId;
  }

  findPets({ force = false } = {}) {
    const params = this.queryParams();
    const key = JSON.stringify(Object.keys(params).sort().map((name) => [name, params[name]]));
    const context = this.listContext();
    const previous = this.listPending;
    if (!force && previous && previous.key === key && this.sameListContext(previous.context, context)) {
      return previous.promise;
    }
    previous?.controller.abort();
    const request = {
      id: this.listRequest = (this.listRequest || 0) + 1,
      key, params, context, controller: new AbortController(),
    };
    this.listPending = request;
    request.promise = this.fetchPets(request);
    return request.promise;
  }

  async fetchPets(request) {
    const isCurrent = () => request.id === this.listRequest &&
      this.sameListContext(request.context, this.listContext());
    try {
      this.actions.setWaiting(true);
      this.actions.setError(null);
      const { data } = await API.get("crm-pet/opportunities", {
        params: request.params,
        signal: request.controller.signal,
      });
      if (!isCurrent() || request.controller.signal.aborted) return null;
      const response = formatOpportunitiesResponse(data);
      if (response.pagination.page >= response.pagination.totalPages && response.pagination.page > 0) {
        this.actions.setPaginationPage(Math.max(0, response.pagination.totalPages - 1));
        return this.findPets();
      }
      this.actions.setCustomers(response.customers, response.pagination, response.summary);
      return response;
    } catch (error) {
      if (!isCurrent()) return null;
      if (request.controller.signal.aborted || error?.code === "ERR_CANCELED" || error?.name === "AbortError") {
        this.actions.setLoading(false);
        this.actions.setWaiting(false);
        return null;
      }
      const message = this.errorMessage(error, "Não foi possível carregar os dados.");
      this.actions.setError(message);
      toast.error(message, { theme: "colored" });
      this.actions.setLoading(false);
      this.actions.setWaiting(false);
      return null;
    } finally {
      // Never clear a newer request, including a corrected pagination request.
      if (this.listPending === request) this.listPending = null;
    }
  }

  async findCustomerOpportunities(customerId) {
    const { data } = await API.get("crm-pet/customer-opportunities", { params: { customerId, minDays: useCrmPet.getState().data.filter.rule === CRM_PET_RULES.ABSENT ? useCrmPet.getState().data.filter.minDays : undefined } });
    return formatCustomerOpportunities(data);
  }

  contactContextParams() {
    const { filter } = useCrmPet.getState().data;
    return filter.rule === CRM_PET_RULES.ABSENT ? { minDays: filter.minDays } : {};
  }

  async setContactStatus(payload) {
    try {
      const { data } = await API.post("crm-pet/contact/status", { ...payload, ...this.contactContextParams() });
      return data;
    } catch (error) {
      toast.error(this.errorMessage(error, "Não foi possível atualizar o contato."), { theme: "colored" });
      return null;
    }
  }

  async findEligibleOpportunities() {
    try {
      this.actions.setOpportunitiesWaiting(true);
      this.actions.setOpportunitiesError(null);
      const { data } = await API.get("crm-pet/recovery-attempts", {
        params: this.eligibleOpportunitiesQueryParams(),
      });

      const currentPagination =
        useCrmPet.getState().data.opportunities.pagination;

      this.actions.setOpportunitiesPets(
        data.results || [],
        this.formatPagination(data, currentPagination),
      );

      return data;
    } catch (error) {
      const message = this.errorMessage(
        error,
        "Não foi possível carregar as oportunidades.",
      );
      this.actions.setOpportunitiesError(message);
      toast.error(message, { theme: "colored" });
      this.actions.setOpportunitiesLoading(false);
      return null;
    }
  }

  async findPendingContacts() {
    try {
      this.actions.setPendingWaiting(true);
      this.actions.setPendingError(null);

      const { data } = await API.get("crm-pet/recovery-attempts", {
        params: this.pendingContactsQueryParams(),
      });

      const currentPagination =
        useCrmPet.getState().data.pending.pagination;

      this.actions.setPendingPets(
        data.results || [],
        this.formatPagination(data, currentPagination),
      );

      return data;
    } catch (error) {
      const message = this.errorMessage(
        error,
        "Não foi possível carregar os contatos pendentes.",
      );
      this.actions.setPendingError(message);
      toast.error(message, { theme: "colored" });
      this.actions.setPendingLoading(false);
      return null;
    }
  }

  async findContactedCustomers() {
    try {
      this.actions.setContactedWaiting(true);
      this.actions.setContactedError(null);

      const { data } = await API.get("crm-pet/recovery-attempts", {
        params: this.contactedCustomersQueryParams(),
      });

      const currentPagination =
        useCrmPet.getState().data.contacted.pagination;

      this.actions.setContactedPets(
        data.results || [],
        this.formatPagination(data, currentPagination),
      );

      return data;
    } catch (error) {
      const message = this.errorMessage(
        error,
        "Não foi possível carregar os clientes contatados.",
      );
      this.actions.setContactedError(message);
      toast.error(message, { theme: "colored" });
      this.actions.setContactedLoading(false);
      return null;
    }
  }

  async registerContact(payload) {
    try {
      const { data } = await API.post("crm-pet/contact", { ...payload, ...this.contactContextParams() });
      return data;
    } catch (error) {
      toast.error(this.errorMessage(error, "Não foi possível registrar o contato."), { theme: "colored" });
      return null;
    }
  }

  async findContactHistory(customer) {
    try {
      const { data } = await API.get("crm-pet/contact/history", {
        params: customer.customerId
          ? { customerId: customer.customerId }
          : { petId: customer.pets?.[0]?.petId },
      });
      const config = useCrmPet.getState().data.config;
      const history = formatContactHistory(data, config);

      this.actions.setContactHistory(history);
      return history;
    } catch (error) {
      this.actions.setContactHistory([]);
      toast.error(this.errorMessage(error, "Não foi possível carregar o histórico de contatos."), { theme: "colored" });
      return [];
    }
  }

  async setBlocked(petId, bloqueado = true) {
    try {
      const { data } = await API.post("crm-pet/block", { petId, bloqueado });
      return data;
    } catch (error) {
      toast.error(this.errorMessage(error, "Não foi possível atualizar o bloqueio."), { theme: "colored" });
      return null;
    }
  }

  async findCustomerActivePets(customerId) {
    try {
      const { data } = await API.get("crm-pet/customer-pets", { params: { customerId } });
      return data;
    } catch {
      return [];
    }
  }

  async findConfig() {
    try {
      const { data } = await API.get("crm-pet/config");
      const config = formatCrmPetConfig({
        ...data,
        faixas: data.faixas?.length ? data.faixas : FAIXAS_DEFAULT,
      });
      this.actions.setConfig(config);
      return config;
    } catch (error) {
      toast.error(this.errorMessage(error, "Não foi possível carregar as configurações."), { theme: "colored" });
      this.actions.setFaixas(FAIXAS_DEFAULT);
      const config = { faixas: FAIXAS_DEFAULT, rules: [] };
      this.actions.setConfig(config);
      return config;
    }
  }

  async saveConfig(config) {
    try {
      const { data } = await API.post("crm-pet/config", config);
      const formattedConfig = formatCrmPetConfig(data);
      this.actions.setConfig(formattedConfig);
      return formattedConfig;
    } catch (error) {
      toast.error(this.errorMessage(error, "Não foi possível salvar as configurações."), { theme: "colored" });
      return null;
    }
  }

  queryParams() {
    const { filter, pagination, sort } = useCrmPet.getState().data;

    const params = {
      page: (pagination.page || 0) + 1,
      perPage: pagination.perPage || 10,
      search: filter.search || "",
      groupBy: "PET",
      analysisDays: 30,
      sortBy: sort.sortBy || "customerName",
      sortOrder: sort.sortOrder || "asc",
    };
    if (filter.rule !== "ALL") params.rule = filter.rule;
    if (filter.rule === CRM_PET_RULES.ABSENT) {
      params.minDays = filter.minDays || CRM_PET_DEFAULT_FILTER.minDays;
    }
    if (filter.contact !== "ALL") params.contact = filter.contact;
    return params;
  }

  eligibleOpportunitiesQueryParams() {
    const { filter, pagination } =
      useCrmPet.getState().data.opportunities;

    const params = {
      page: (pagination.page || 0) + 1,
      perPage: pagination.perPage || 10,
      status: "PENDING_CONTACT,CONTACTED,CONVERTED_WITHOUT_CONTACT",
    };

    if (filter.from) params.from = filter.from;
    if (filter.to) params.to = filter.to;

    return params;
  }

  pendingContactsQueryParams() {
    const { filter, pagination } =
      useCrmPet.getState().data.pending;

    const params = {
      page: (pagination.page || 0) + 1,
      perPage: pagination.perPage || 10,
      status: "PENDING_CONTACT",
    };

    if (filter.from) params.from = filter.from;
    if (filter.to) params.to = filter.to;

    return params;
  }

  contactedCustomersQueryParams() {
    const { filter, pagination } =
      useCrmPet.getState().data.contacted;
    const minDays = this.resolveMinDays(filter);

    const params = {
      page: (pagination.page || 0) + 1,
      perPage: pagination.perPage || 10,
      contacted: "true",
      minDays,
    };

    if (filter.from) params.from = filter.from;
    if (filter.to) params.to = filter.to;

    return params;
  }

  resolveMinDays(filter = CRM_PET_DEFAULT_FILTER) {
    if (filter.diasFiltro === "all") return 0;
    if (filter.diasFiltro === "custom") return parseInt(filter.customDays, 10) || 30;
    return parseInt(filter.diasFiltro, 10) || 30;
  }

  resolveWithSubscription(filter = CRM_PET_DEFAULT_FILTER) {
    if (filter.withSubscription === "sim") return true;
    if (filter.withSubscription === "nao") return false;
    return null;
  }

  formatPagination(
    data,
    currentPagination = useCrmPet.getState().data.pagination,
  ) {
    const pagination = data.pagination || {};
    const page = currentPagination.page || 0;
    const perPage = pagination.perPage || data.perPage || currentPagination.perPage;
    const totalDocuments = pagination.totalDocuments || data.total || data.results?.length || 0;
    const totalPages = pagination.totalPages || Math.ceil(totalDocuments / perPage) || 1;

    return {
      page,
      perPage,
      totalDocuments,
      totalPages,
    };
  }

  formatSummary(data) {
    const pets = data.results || [];
    const summary = data.summary || {};

    return {
      totalAll: summary.totalAll ?? summary.total ?? summary.totalClientes ?? data.total ?? pets.length,
      realizados: summary.realizados ?? summary.totalRealizados ?? pets.filter((pet) => pet.contatoRealizado && !pet.bloqueado).length,
      bloqueados: summary.bloqueados ?? summary.totalBloqueados ?? pets.filter((pet) => pet.bloqueado).length,
      pendentes: summary.pendentes ?? summary.totalPendentes ?? pets.filter((pet) => !pet.contatoRealizado && !pet.bloqueado).length,
    };
  }

  errorMessage(error, fallback) {
    return error?.response?.data?.friendly_message || error?.message || fallback;
  }
}

let MCrmPet = null;

export function CrmPetModel() {
  if (MCrmPet) return MCrmPet;

  MCrmPet = new CrmPet();
  return MCrmPet;
}
